// Lib
import { useCallback } from 'react';
import { Events } from 'handsontable/pluginHooks';
import Handsontable from 'handsontable/base';
import useDebouncedCallback from '../../../../utils/react/useDebouncedCallback';

// Utils
import { getHotColWidthsGu } from '../utils/hotInstanceUtils/hotTableGridDimensionsUtils';
import { getHotTableData } from '../utils/hotInstanceUtils/hotTableDataUtilts';

// Constants
import { TABLE_DEFAULT_COL_WIDTH, TABLE_DEFAULT_ROW_HEIGHT } from '../../../../../common/table/tableConstants';

interface UseTableColumnResizeHandlersProps {
    elementId: string;
    gridSize: number;

    dispatchUpdateTableElement: (args: object) => void;
    hotTableInstanceRef: React.MutableRefObject<Handsontable.Core | null>;
}

export interface UseTableColumnResizeHandlersReturn {
    afterColumnResize: Events['afterColumnResize'];
    getDefaultColWidth: () => number;
    getDefaultRowHeight: (index: number) => number | undefined;
}

/**
 * Custom hook for handling table column resize operations
 *
 * @param props - Hook configuration
 * @returns Handler functions for column resizing
 */
export const useTableColumnResizeHandlers = (
    props: UseTableColumnResizeHandlersProps,
): UseTableColumnResizeHandlersReturn => {
    const { elementId, gridSize, dispatchUpdateTableElement, hotTableInstanceRef } = props;

    const afterColumnResize = useDebouncedCallback(
        (newSize: number, column: number, isDoubleClick: boolean): void => {
            const manualColumnResizePlugin =
                hotTableInstanceRef.current?.getPlugin<'manualColumnResize'>('manualColumnResize');

            if (!manualColumnResizePlugin) return;

            const hotTableData = getHotTableData(hotTableInstanceRef.current);
            const hotColWidthsGU = getHotColWidthsGu(hotTableInstanceRef.current, gridSize);

            if (!hotColWidthsGU) return;

            dispatchUpdateTableElement({
                id: elementId,
                changes: {
                    tableContent: {
                        data: hotTableData,
                        colWidthsGU: hotColWidthsGU,
                    },
                },
            });
        },
        10,
        [gridSize, elementId, hotTableInstanceRef, dispatchUpdateTableElement],
    );

    /**
     * Keep the min col width value accurate in the hot table.
     */
    const getDefaultColWidth = useCallback(() => {
        return TABLE_DEFAULT_COL_WIDTH * gridSize;
    }, [gridSize]);

    /**
     * Keep the min row height value accurate in the hot table.
     */
    const getDefaultRowHeight = useCallback(
        (index: number) => {
            if (!hotTableInstanceRef.current) return;
            const nRows = hotTableInstanceRef.current.countRows();
            const offset = index === nRows - 1 ? 1 : 0;
            return TABLE_DEFAULT_ROW_HEIGHT * gridSize - offset;
        },
        [gridSize, hotTableInstanceRef],
    );

    return {
        afterColumnResize,
        getDefaultColWidth,
        getDefaultRowHeight,
    };
};

export default useTableColumnResizeHandlers;
